import { bindAcquisitionOfferActions, bindAcquisitionPipelineActions } from "./dashboard-acquisition-actions.js";
import { bindContactOutreachActions, submitOutreachAttempt, submitRetryLetterAddressVerification } from "./dashboard-contact-outreach-actions.js";
import { bindDocumentThroughputOutputActions, submitSourceDocumentReviewDisposition } from "./dashboard-document-throughput-actions.js";
import { bindDownstreamAcquisitionActions } from "./dashboard-downstream-actions.js";
import { bindTaskAssignmentActions } from "./dashboard-fact-correction-actions.js";
import { bindLeadGenerationBulkActions } from "./dashboard-lead-generation-actions.js";
import { submitLeadAction } from "./dashboard-lead-actions.js";
import { showLeadDetail } from "./dashboard-lead-detail-nav.js";
import type { SourceDocumentReviewFocus } from "./dashboard-lead-factory-types.js";
import {
  submitLeadSupplyManualEvidenceRequestAlternateResearch,
  submitLeadSupplyManualEvidenceRequestCorrection,
  submitLeadSupplyManualEvidenceRequestStatus,
  submitLeadSupplyManualEvidenceRequestUpload,
  submitLeadSupplyManualEvidenceStatus,
  submitPropertyAddressResearch
} from "./dashboard-manual-evidence-actions.js";
import { bindMortgageEvidenceReviewActionForms } from "./dashboard-mortgage-evidence-actions.js";
import { bindOwnerContactEnrichmentActions } from "./dashboard-owner-contact-actions.js";
import type { DashboardActionHost, ReferencedDeedCaptureInput } from "./dashboard-referenced-deed-actions.js";
import {
  submitLeadSupplyReferencedDeedDirectFetch,
  submitLeadSupplyReferencedDeedRetrieval,
  submitLeadSupplyReferencedDeedRetrievalReview,
  submitLeadSupplyReferencedDeedRunnerQueue,
  submitLeadSupplyReferencedDeedUpload,
  submitLeadSupplyReferencedOriginalOcrQueue
} from "./dashboard-referenced-deed-actions.js";
import { bindReviewQueueBulkActions } from "./dashboard-review-queue-actions.js";
import {
  bindBusinessMetricsWindowControls,
  bindOperationalSavedViewControls
} from "./dashboard-saved-view-controls.js";
import { bindManualSourceIntakeActions, bindSourceBlockerDecisionActions } from "./dashboard-source-blocker-actions.js";
import { bindSourceDocumentReviewActions } from "./dashboard-source-review-preview.js";
import {
  submitOwnedPropertyBulkComplete,
  submitOwnedPropertyTaskComplete,
  submitTaskComplete
} from "./dashboard-task-complete-actions.js";

let leadFactoryActionStatusElement: HTMLElement | null = null;
let leadFactoryActionDelegationBound = false;
let boundActions: DashboardActionHost | null = null;

export function ensureLeadDetailNavigationBound(statusElement: HTMLElement): void {
  leadFactoryActionStatusElement = statusElement;
  bindDelegatedLeadFactoryActions();
}

export function bindLeadFactoryActions(
  statusElement: HTMLElement,
  actions: DashboardActionHost,
  showReview: (sourceDocumentId: string, statusElement: HTMLElement, focus?: SourceDocumentReviewFocus) => Promise<void>
): void {
  boundActions = actions;
  leadFactoryActionStatusElement = statusElement;
  bindDelegatedLeadFactoryActions();
  bindSourceBlockerDecisionActions(statusElement, actions.refresh);
  bindManualSourceIntakeActions(statusElement, actions.refresh);
  bindBusinessMetricsWindowControls(statusElement, actions);
  bindOperationalSavedViewControls(statusElement, actions);
  bindSourceDocumentReviewActions(statusElement, showReview);
  bindLeadGenerationBulkActions(statusElement, actions);
  bindDocumentThroughputOutputActions(statusElement, actions, showReview);
  bindMortgageEvidenceReviewActionForms(statusElement, actions);
  bindOwnerContactEnrichmentActions(statusElement, actions);
  document.querySelectorAll<HTMLFormElement>("[data-lead-supply-referenced-dt-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const sourceDocumentId = form.dataset.sourceDocumentId;
      if (!sourceDocumentId) return;
      const capture: ReferencedDeedCaptureInput = {};
      const referencedDeedBook = form.querySelector<HTMLInputElement>("[data-lead-supply-referenced-dt-book]")?.value;
      const referencedDeedPage = form.querySelector<HTMLInputElement>("[data-lead-supply-referenced-dt-page]")?.value;
      const sourcePage = form.querySelector<HTMLInputElement>("[data-lead-supply-referenced-dt-source-page]")?.value;
      const evidenceExcerpt = form.querySelector<HTMLInputElement>("[data-lead-supply-referenced-dt-evidence]")?.value;
      if (referencedDeedBook) capture.referencedDeedBook = referencedDeedBook;
      if (referencedDeedPage) capture.referencedDeedPage = referencedDeedPage;
      if (sourcePage) capture.sourcePage = sourcePage;
      if (evidenceExcerpt) capture.evidenceExcerpt = evidenceExcerpt;
      void submitLeadSupplyReferencedDeedRetrieval(sourceDocumentId, form.dataset.leadId, statusElement, capture, actions);
    });
    form.querySelector<HTMLButtonElement>("[data-lead-supply-referenced-dt-upload]")?.addEventListener("click", () => {
      void submitLeadSupplyReferencedDeedUpload(form, statusElement, actions);
    });
    form.querySelector<HTMLButtonElement>("[data-lead-supply-referenced-dt-direct]")?.addEventListener("click", () => {
      void submitLeadSupplyReferencedDeedDirectFetch(form, statusElement, actions);
    });
    form.querySelector<HTMLButtonElement>("[data-lead-supply-referenced-dt-queue]")?.addEventListener("click", () => {
      void submitLeadSupplyReferencedDeedRunnerQueue(form, statusElement, actions);
    });
    form.querySelector<HTMLButtonElement>("[data-lead-supply-referenced-dt-review]")?.addEventListener("click", () => {
      void submitLeadSupplyReferencedDeedRetrievalReview(form, statusElement, actions);
    });
    form.querySelector<HTMLButtonElement>("[data-lead-supply-manual-evidence-status-action]")?.addEventListener("click", () => {
      void submitLeadSupplyManualEvidenceStatus(form, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-lead-supply-manual-evidence-request-status-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void submitLeadSupplyManualEvidenceRequestStatus(form, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-lead-supply-manual-evidence-request-upload-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void submitLeadSupplyManualEvidenceRequestUpload(form, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-lead-supply-manual-evidence-request-correction-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void submitLeadSupplyManualEvidenceRequestCorrection(form, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-lead-supply-manual-evidence-request-alternate-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void submitLeadSupplyManualEvidenceRequestAlternateResearch(form, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-lead-supply-fetch-referenced-dt]").forEach((button) => {
    button.addEventListener("click", () => {
      const sourceDocumentId = button.dataset.leadSupplyFetchReferencedDt;
      if (sourceDocumentId) void submitLeadSupplyReferencedDeedRetrieval(sourceDocumentId, button.dataset.leadId, statusElement, undefined, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-lead-supply-referenced-original-ocr-queue]").forEach((button) => {
    button.addEventListener("click", () => {
      const sourceDocumentId = button.dataset.leadSupplyReferencedOriginalOcrQueue;
      if (sourceDocumentId) void submitLeadSupplyReferencedOriginalOcrQueue(sourceDocumentId, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-source-document-review-disposition]").forEach((button) => {
    button.addEventListener("click", () => {
      const sourceDocumentId = button.dataset.sourceDocumentId;
      const outcome = button.dataset.sourceDocumentReviewDisposition;
      if (sourceDocumentId && outcome) void submitSourceDocumentReviewDisposition(sourceDocumentId, outcome, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-lead-supply-property-address-research-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void submitPropertyAddressResearch(form, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-task-complete]").forEach((button) => {
    button.addEventListener("click", () => {
      const taskId = button.dataset.taskComplete;
      if (taskId) void submitTaskComplete(taskId, statusElement, actions);
    });
  });
  bindTaskAssignmentActions(statusElement, actions);
  bindReviewQueueBulkActions(statusElement, actions);
  document.querySelectorAll<HTMLButtonElement>("[data-owned-property-task-complete]").forEach((button) => {
    button.addEventListener("click", () => {
      const taskId = button.dataset.ownedPropertyTaskComplete;
      if (taskId) void submitOwnedPropertyTaskComplete(taskId, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-owned-property-bulk-complete]").forEach((button) => {
    button.addEventListener("click", () => {
      const taskIds = (button.dataset.ownedPropertyBulkComplete || "").split(",").map((value) => value.trim()).filter(Boolean);
      if (taskIds.length > 0) void submitOwnedPropertyBulkComplete(taskIds, button.dataset.ownedPropertyBulkLeadId || "owned-property", statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-lead-outreach]").forEach((button) => {
    button.addEventListener("click", () => {
      const leadId = button.dataset.leadOutreach;
      if (!leadId) return;
      void submitOutreachAttempt(
        leadId,
        button.dataset.outreachChannel || "letter",
        button.dataset.outreachOutcome || "sent",
        statusElement,
        actions
      );
    });
  });
  bindContactOutreachActions(statusElement, actions);
  bindAcquisitionPipelineActions(statusElement);
  bindAcquisitionOfferActions(statusElement);
  bindDownstreamAcquisitionActions(statusElement);
}

function bindDelegatedLeadFactoryActions(): void {
  if (leadFactoryActionDelegationBound) return;
  leadFactoryActionDelegationBound = true;
  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const anchor = target?.closest<HTMLAnchorElement>('a[href^="#"]');
    if (!anchor) return;
    const detail = document.querySelector<HTMLElement>("#county-detail");
    if (detail) detail.innerHTML = "";
  });
  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest<HTMLButtonElement>("[data-lead-action]");
    if (!button) return;
    const leadId = button.dataset.leadId;
    const action = button.dataset.leadAction;
    const statusElement = document.querySelector<HTMLElement>("#action-status") ?? leadFactoryActionStatusElement;
    if (!leadId || !action || !statusElement) return;
    event.preventDefault();
    if (action === "detail") {
      void showLeadDetail(leadId, statusElement, leadDetailHost());
      return;
    }
    void submitLeadAction(leadId, action, statusElement, currentActions());
  });
  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest<HTMLButtonElement>("[data-contact-work-retry-address-verify]");
    if (!button) return;
    const leadId = button.dataset.leadId?.trim() || "";
    const contactMethodId = button.dataset.contactMethodId?.trim() || "";
    const statusElement = document.querySelector<HTMLElement>("#action-status") ?? leadFactoryActionStatusElement;
    if (!leadId || !contactMethodId || !statusElement) return;
    event.preventDefault();
    void submitRetryLetterAddressVerification(leadId, contactMethodId, statusElement, currentActions());
  });
}

function currentActions(): DashboardActionHost {
  return boundActions ?? { refresh() {}, onAuthRequired() {} };
}

function leadDetailHost() {
  return {
    ...currentActions(),
    bindNavigation: ensureLeadDetailNavigationBound
  };
}
